/**
 * Requirement Builder Page
 *
 * Create and edit project requirements with acceptance criteria.
 */

import { A, useNavigate, useParams } from '@solidjs/router';
import { type Component, createResource, createSignal, For, onMount, Show } from 'solid-js';
import {
  type AcceptanceCriteria,
  createRequirement,
  fetchProject,
  fetchRequirement,
  type RequirementPriority,
  type RequirementStatus,
  updateRequirement,
} from '../services/api';

const RequirementBuilder: Component = () => {
  const params = useParams();
  const navigate = useNavigate();
  const isEditing = () => !!params.reqId;

  // Fetch project for context
  // Note: We wrap fetchProject to avoid SolidJS passing { value, refetching } as the retries parameter
  const [project] = createResource(() => params.id, (projectId) => fetchProject(projectId));

  // Fetch existing requirement if editing
  const [existingRequirement] = createResource(
    () => params.reqId,
    (id) => (id ? fetchRequirement(id) : null),
  );

  // Form state
  const [name, setName] = createSignal('');
  const [description, setDescription] = createSignal('');
  const [priority, setPriority] = createSignal<RequirementPriority>('medium');
  const [status, setStatus] = createSignal<RequirementStatus>('draft');
  const [dueDate, setDueDate] = createSignal('');
  const [estimatedEffort, setEstimatedEffort] = createSignal('');
  const [category, setCategory] = createSignal('');
  const [tags, setTags] = createSignal('');
  const [acceptanceCriteria, setAcceptanceCriteria] = createSignal<AcceptanceCriteria[]>([]);

  // New criteria input
  const [newCriteriaText, setNewCriteriaText] = createSignal('');

  // UI state
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  // Populate form when editing
  onMount(() => {
    if (isEditing() && existingRequirement()) {
      const req = existingRequirement()!;
      setName(req.name);
      setDescription(req.description || '');
      setPriority(req.priority);
      setStatus(req.status);
      setDueDate(req.dueDate ? new Date(req.dueDate).toISOString().split('T')[0] : '');
      setEstimatedEffort(req.estimatedEffort || '');
      setCategory(req.category || '');
      setTags(req.tags?.join(', ') || '');
      setAcceptanceCriteria(req.acceptanceCriteria || []);
    }
  });

  // Also watch for data changes after initial mount
  createResource(
    () => existingRequirement(),
    (req) => {
      if (req && isEditing()) {
        setName(req.name);
        setDescription(req.description || '');
        setPriority(req.priority);
        setStatus(req.status);
        setDueDate(req.dueDate ? new Date(req.dueDate).toISOString().split('T')[0] : '');
        setEstimatedEffort(req.estimatedEffort || '');
        setCategory(req.category || '');
        setTags(req.tags?.join(', ') || '');
        setAcceptanceCriteria(req.acceptanceCriteria || []);
      }
    },
  );

  const addCriteria = () => {
    if (!newCriteriaText().trim()) return;
    setAcceptanceCriteria([
      ...acceptanceCriteria(),
      {
        id: crypto.randomUUID(),
        description: newCriteriaText().trim(),
        completed: false,
      },
    ]);
    setNewCriteriaText('');
  };

  const removeCriteria = (id: string) => {
    setAcceptanceCriteria(acceptanceCriteria().filter((c) => c.id !== id));
  };

  const toggleCriteriaComplete = (id: string) => {
    setAcceptanceCriteria(
      acceptanceCriteria().map((c) =>
        c.id === id
          ? {
              ...c,
              completed: !c.completed,
              completedAt: !c.completed ? new Date().toISOString() : undefined,
            }
          : c,
      ),
    );
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const data = {
        projectId: params.id,
        name: name(),
        description: description() || undefined,
        priority: priority(),
        status: status(),
        dueDate: dueDate() ? new Date(dueDate()).toISOString() : undefined,
        estimatedEffort: estimatedEffort() || undefined,
        category: category() || undefined,
        tags: tags()
          ? tags()
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : undefined,
        acceptanceCriteria: acceptanceCriteria(),
      };

      if (isEditing()) {
        await updateRequirement(params.reqId!, data);
      } else {
        await createRequirement(data);
      }

      navigate(`/projects/${params.id}?tab=requirements`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save requirement');
    } finally {
      setSaving(false);
    }
  };

  const getPriorityColor = (p: RequirementPriority) => {
    switch (p) {
      case 'critical':
        return 'text-red-400';
      case 'high':
        return 'text-orange-400';
      case 'medium':
        return 'text-yellow-400';
      case 'low':
        return 'text-gray-400';
    }
  };

  return (
    <div class="max-w-3xl mx-auto">
      {/* Header */}
      <div class="mb-6">
        <div class="flex items-center gap-4 mb-2">
          <A
            href={`/projects/${params.id}`}
            class="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
          </A>
          <div>
            <h1 class="text-2xl font-semibold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
              {isEditing() ? 'Edit Requirement' : 'New Requirement'}
            </h1>
            <Show when={project()}>
              <p class="text-gray-400 text-sm mt-1">Project: {project()?.name}</p>
            </Show>
          </div>
        </div>
      </div>

      {/* Error */}
      <Show when={error()}>
        <div class="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl">
          <p class="text-red-400">{error()}</p>
        </div>
      </Show>

      <form onSubmit={handleSubmit} class="space-y-6">
        {/* Basic Info */}
        <div class="bg-gray-800/50 border border-gray-700 rounded-xl p-6 space-y-4">
          <h2 class="text-lg font-medium text-gray-200 mb-4">Basic Information</h2>

          <div>
            <label class="block text-sm font-medium text-gray-300 mb-1.5">
              Name <span class="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name()}
              onInput={(e) => setName(e.currentTarget.value)}
              placeholder="e.g., User Authentication System"
              required
              class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-300 mb-1.5">Description</label>
            <textarea
              value={description()}
              onInput={(e) => setDescription(e.currentTarget.value)}
              placeholder="Describe what this requirement entails..."
              rows={4}
              class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-300 mb-1.5">Status</label>
            <select
              value={status()}
              onChange={(e) => setStatus(e.currentTarget.value as RequirementStatus)}
              class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="deprecated">Deprecated</option>
            </select>
          </div>
        </div>

        {/* Acceptance Criteria */}
        <div class="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
          <h2 class="text-lg font-medium text-gray-200 mb-4">Acceptance Criteria</h2>
          <p class="text-gray-500 text-sm mb-4">
            Define measurable criteria that must be met for this requirement to be complete.
          </p>

          {/* Add new criteria */}
          <div class="flex gap-2 mb-4">
            <input
              type="text"
              value={newCriteriaText()}
              onInput={(e) => setNewCriteriaText(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCriteria();
                }
              }}
              placeholder="Add acceptance criteria..."
              class="flex-1 px-4 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <button
              type="button"
              onClick={addCriteria}
              class="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
            >
              Add
            </button>
          </div>

          {/* Criteria list */}
          <Show when={acceptanceCriteria().length > 0}>
            <div class="space-y-2">
              <For each={acceptanceCriteria()}>
                {(criteria) => (
                  <div
                    class={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      criteria.completed
                        ? 'bg-green-500/10 border-green-500/30'
                        : 'bg-gray-900/50 border-gray-700'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleCriteriaComplete(criteria.id)}
                      class={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        criteria.completed
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'border-gray-500 hover:border-amber-500'
                      }`}
                    >
                      <Show when={criteria.completed}>
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="3"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </Show>
                    </button>
                    <span
                      class={`flex-1 ${
                        criteria.completed ? 'text-gray-400 line-through' : 'text-gray-200'
                      }`}
                    >
                      {criteria.description}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeCriteria(criteria.id)}
                      class="text-gray-500 hover:text-red-400 transition-colors"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </For>
            </div>
          </Show>

          <Show when={acceptanceCriteria().length === 0}>
            <div class="text-center py-6 text-gray-500">
              <p>No acceptance criteria defined yet.</p>
            </div>
          </Show>
        </div>

        {/* Categorization */}
        <div class="bg-gray-800/50 border border-gray-700 rounded-xl p-6 space-y-4">
          <h2 class="text-lg font-medium text-gray-200 mb-4">Categorization</h2>

          <div>
            <label class="block text-sm font-medium text-gray-300 mb-1.5">Category</label>
            <input
              type="text"
              value={category()}
              onInput={(e) => setCategory(e.currentTarget.value)}
              placeholder="e.g., Authentication, UI, Backend"
              class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-300 mb-1.5">Tags</label>
            <input
              type="text"
              value={tags()}
              onInput={(e) => setTags(e.currentTarget.value)}
              placeholder="security, mvp, phase-1 (comma-separated)"
              class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
        </div>

        {/* Actions */}
        <div class="flex gap-4">
          <A
            href={`/projects/${params.id}`}
            class="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-xl transition-colors text-center"
          >
            Cancel
          </A>
          <button
            type="submit"
            disabled={saving() || !name().trim()}
            class="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium rounded-xl transition-all disabled:opacity-50"
          >
            {saving() ? 'Saving...' : isEditing() ? 'Update Requirement' : 'Create Requirement'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RequirementBuilder;
